import { IconeAvancar, IconeVoltar } from './Icones.js';
import './Paginacao.css';

type Props = {
  /** Texto do intervalo mostrado, ex.: "1–14 de 1.284 registros". */
  readonly intervalo: string;
  readonly pagina: number;
  readonly totalPaginas: number;
  readonly aoIrPara: (pagina: number) => void;
};

/**
 * Páginas visíveis: sempre a primeira e a última, mais uma janela em volta da
 * atual, com reticências onde houver corte. Listagens do painel passam de 90
 * páginas — numerar todas seria ruído.
 *
 * A primeira página é fixa de propósito. Antes a janela começava na página
 * atual, então a partir da página 2 o número 1 sumia da barra: sem botão de
 * voltar e sem o 1, quem avançasse ficava sem caminho de volta pela paginação.
 */
function paginasVisiveis(pagina: number, total: number): readonly (number | 'salto')[] {
  if (total <= 5) {
    return Array.from({ length: total }, (_, indice) => indice + 1);
  }

  const atual = Math.min(Math.max(pagina, 1), total);
  // Janela de três consecutivas, presa entre a segunda e a penúltima: nas
  // bordas ela desliza para dentro em vez de encolher, para a barra não mudar
  // de largura conforme se navega.
  const inicio = Math.min(Math.max(atual - 1, 2), total - 3);
  const vizinhas = [inicio, inicio + 1, inicio + 2].filter((n) => n > 1 && n < total);
  const numeros = [1, ...vizinhas, total];

  const comSaltos: (number | 'salto')[] = [];
  numeros.forEach((numero, indice) => {
    const anterior = numeros[indice - 1];
    if (anterior !== undefined && numero - anterior > 1) {
      comSaltos.push('salto');
    }
    comSaltos.push(numero);
  });

  return comSaltos;
}

export function Paginacao({ intervalo, pagina, totalPaginas, aoIrPara }: Props) {
  return (
    <>
      <span className="paginacao__intervalo">{intervalo}</span>
      <nav className="paginacao" aria-label="Paginação">
        <button
          type="button"
          className="paginacao__pagina"
          aria-label="Página anterior"
          disabled={pagina <= 1}
          onClick={() => {
            aoIrPara(pagina - 1);
          }}
        >
          <IconeVoltar tamanho={14} />
        </button>
        {paginasVisiveis(pagina, totalPaginas).map((item, indice) =>
          item === 'salto' ? (
            <span key={`salto-${String(indice)}`} className="paginacao__salto">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              className={
                item === pagina ? 'paginacao__pagina paginacao__pagina--atual' : 'paginacao__pagina'
              }
              aria-label={`Página ${String(item)}`}
              aria-current={item === pagina ? 'page' : undefined}
              onClick={() => {
                aoIrPara(item);
              }}
            >
              {item}
            </button>
          ),
        )}
        <button
          type="button"
          className="paginacao__pagina"
          aria-label="Próxima página"
          disabled={pagina >= totalPaginas}
          onClick={() => {
            aoIrPara(pagina + 1);
          }}
        >
          <IconeAvancar tamanho={14} />
        </button>
      </nav>
    </>
  );
}
